import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import * as fileSaver from 'file-saver';
import { UoDialogComponent } from './uo-dialog/uo-dialog.component';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { MatConfirmDialogComponent } from 'src/app/shared/mat-confirm-dialog/mat-confirm-dialog.component';
import { IUniteOrganisationnelle } from 'src/app/shared/models/up-evaluation';
import { UniteOrganisationnelleService } from './uo-evaluation.service';

@Component({
  selector: 'app-uo-list',
  templateUrl: './uo-evaluation.component.html',
  styleUrls: ['./uo-evaluation.component.less']
})
export class UoListComponent implements OnInit, AfterViewInit {

  dataSource = new MatTableDataSource<IUniteOrganisationnelle>();
  displayedColumns: string[] = ['code', 'libelle', 'actions'];
  loading = false;

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private uoService: UniteOrganisationnelleService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private _liveAnnouncer: LiveAnnouncer,
    private router: Router
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadUos();
  }

  goBack(): void {
    this.router.navigate(['/core/admin/centre-competence']);
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => (item as any)[property];
  }

  announceSortChange(sortState: Sort): void {
    if (sortState.direction) {
      this._liveAnnouncer.announce(
        `Tri ${sortState.direction === 'asc' ? 'ascendant' : 'descendant'}`
      );
    } else {
      this._liveAnnouncer.announce('Tri réinitialisé');
    }
  }

  // ── Chargement ────────────────────────────────────────────────────────────

  loadUos(): void {
    this.loading = true;
    this.uoService.getAll().subscribe({
      next: (data) => {
        this.dataSource.data = data;
        this.loading = false;
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors du chargement des UO'),
          'error-snack-bar'
        );
        this.loading = false;
      }
    });
  }

  // ── Filtre ────────────────────────────────────────────────────────────────

  applyFilter(event: Event): void {
    this.dataSource.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
  }

  // ── Dialog ────────────────────────────────────────────────────────────────

  openDialog(uo: IUniteOrganisationnelle | null = null): void {
    const dialogRef = this.dialog.open(UoDialogComponent, {
      width: '500px',
      maxWidth: '95vw',
      data: uo
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadUos();
    });
  }

  // ── Suppression ───────────────────────────────────────────────────────────

  deleteUo(uo: IUniteOrganisationnelle): void {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: { message: `Supprimer l'UO "${uo.code} — ${uo.libelle}" ? Les compétences et évaluations associées perdront leur lien.` }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.uoService.delete(uo.id).subscribe({
        next: () => {
          this.snackbarService.openSnackBar('Unité organisationnelle supprimée avec succès');
          this.dataSource.data = this.dataSource.data.filter(u => u.id !== uo.id);
        },
        error: (err) => {
          this.snackbarService.openSnackBar(
            this.extractErrorMessage(err, 'Erreur lors de la suppression'),
            'error-snack-bar'
          );
        }
      });
    });
  }

  // ── Export ────────────────────────────────────────────────────────────────

  exportData(): void {
    this.uoService.exportData().subscribe({
      next: (data) => {
        if (!data) {
          this.snackbarService.openSnackBar('Aucune unité organisationnelle à exporter', 'error-snack-bar');
          return;
        }
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        fileSaver.saveAs(
          blob,
          'Unites_Organisationnelles_' +
          new Date().toLocaleDateString().replace(/\//g, '-') +
          '.xlsx'
        );
        this.snackbarService.openSnackBar('Les unités organisationnelles ont été exportées avec succès');
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'export des UO'),
          'error-snack-bar'
        );
      }
    });
  }

  // ── Helper erreurs ────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}